import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { dashboardClient, filteredCustomersClient, useProgressiveLoading } from '../utils/optimizedApi';
import { generateCampaignName, getEmptyCampaignForm } from '../utils/campaignNameGenerator';
import axios from 'axios';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import StatsSection from '../components/DashboardComponents/StatsSection';
import FiltersContainer from '../components/DashboardComponents/FiltersContainer';
import UploadSection from '../components/DashboardComponents/UploadSection';
import CampaignPopup from '../components/campaigns/CampaignPopup';
import ConfirmationPopup from '../components/DashboardComponents/ConfirmationPopup';
import CampaignDownloadModal from '../components/campaigns/CampaignDownloadModal';
import DownloadFormatModal from '../components/campaigns/DownloadFormatModal';
import Alert from '../components/common/Alert';
import { toLimechatCSV, toEmailOnlyCSV, extractEmailsFromCSV } from '../utils/campaignDownloadFormats';
import './Dashboard.css';

// Note: Hub and City mapping is now fetched from API response
// Use paths relative to axios.defaults.baseURL (/api) — do NOT prefix REACT_APP_API_URL
// or requests become /api/api/... and fail with a generic error toast.

const getApiErrorMessage = (error, fallback) => {
  const data = error.response?.data;
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (typeof data === 'string' && data.trim()) {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.message) return parsed.message;
    } catch {
      if (error.response?.status === 413) return 'Upload too large (413). Ask admin to raise Nginx client_max_body_size.';
      if (error.response?.status === 404) return 'API route not found (404). Frontend may be calling the wrong path.';
      if (error.response?.status >= 500) return `Server error (${error.response.status}). Check backend logs.`;
    }
  }
  if (error.response?.status === 413) return 'Request too large (413). Check Nginx client_max_body_size.';
  if (error.response?.status === 401) return 'Session expired. Please log in again.';
  if (error.response?.status === 404) return 'API route not found (404).';
  if (error.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (!error.response) return 'Network error. Check connection or server status.';
  return fallback;
};

const formatGroupDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.round(Number(ms) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours} hr ${minutes} min`;
  if (minutes > 0) return `${minutes} min ${seconds} sec`;
  return `${seconds} sec`;
};

const formatGroupCount = (value) => Number(value || 0).toLocaleString('en-IN');

const formatUserGroupProgress = (data) => {
  if (!data || data.status === 'preparing' || !data.total) return 'Preparing the customer list...';
  const progress = `${formatGroupCount(data.done)} / ${formatGroupCount(data.total)}`;
  const elapsed = formatGroupDuration(data.elapsedMs);
  if (data.remainingMs) return `${progress} · ${elapsed} · about ${formatGroupDuration(data.remainingMs)} left`;
  return `${progress} · ${elapsed} · calculating time left`;
};

const formatUserGroupSuccess = (data) => {
  const parts = [`${formatGroupCount(data.added)} users added`];
  if (data.skipped) parts.push(`${formatGroupCount(data.skipped)} already saved earlier`);
  if (data.alreadyInGroup) parts.push(`${formatGroupCount(data.alreadyInGroup)} already in the group`);
  if (data.failed) parts.push(`${formatGroupCount(data.failed)} failed`);
  return `User group "${data.groupName}" saved. ${parts.join(', ')}.`;
};

const INITIAL_FILTERS_STATE = {
  updatedAtFrom: '',
  updatedAtTo: '',
  createdOnFrom: '',
  createdOnTo: '',
  firstOrderFrom: '',
  firstOrderTo: '',
  lastOrderFrom: '',
  lastOrderTo: '',
  lastOrderExcludeFrom: '',
  lastOrderExcludeTo: '',
  lifetimeAOVMin: '',
  lifetimeAOVMax: '',
  lifetimeGMVMin: '',
  lifetimeGMVMax: '',
  totalOrdersMin: '',
  totalOrdersMax: '',
  abandonedCartValueMin: '',
  abandonedCartValueMax: '',
  abandonedCartLastUpdatedFrom: '',
  abandonedCartLastUpdatedTo: '',
  campaignName: '',
  campaignDateFrom: '',
  campaignDateTo: '',
  substore: '',
  emailSearch: '',
  phoneSearch: '',
  appUserType: '',
  customer: ''
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { loadingStates, loadCriticalData, loadSecondaryData } = useProgressiveLoading();
  
  const [stats, setStats] = useState({
    totalCustomers: 0,
    customersWithOrders: 0,
    totalOrders: 0,
    newThisMonth: 0,
    customersWithAbandonedCarts: 0,
    totalAbandonedCartValue: 0,
    customersWithCampaigns: 0,
    topSubstores: []
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFilters, setShowFilters] = useState(true); // Show filters by default
  const [substores, setSubstores] = useState([]);
  const [citySubstores, setCitySubstores] = useState({});
  const [cityHubs, setCityHubs] = useState({});
  const [hubToSubstoreMapping, setHubToSubstoreMapping] = useState([]);
  const [cities, setCities] = useState([]);
  const [campaignNames, setCampaignNames] = useState([]);
  const [filters, setFilters] = useState(() => ({ ...INITIAL_FILTERS_STATE }));
  
  // Location filter state (replaces substore multi-select)
  const [selectedLocations, setSelectedLocations] = useState({
    inScope: {
      delhi: [],
      bangalore: [],
      blank: false
    },
    outScope: []
  });
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    inScope: false,
    outScope: false
  });
  const [expandedCities, setExpandedCities] = useState({
    delhi: false,
    bangalore: false
  });
  
  // Campaign name multi-select state
  const [selectedCampaignNames, setSelectedCampaignNames] = useState([]);
  const [campaignNameSearchTerm, setCampaignNameSearchTerm] = useState('');
  const [showCampaignNameDropdown, setShowCampaignNameDropdown] = useState(false);
  const [filteredResults, setFilteredResults] = useState(null);
  const [userGroupName, setUserGroupName] = useState('');
  const [creatingUserGroup, setCreatingUserGroup] = useState(false);
  const [userGroupProgress, setUserGroupProgress] = useState(null);
  const userGroupPollRef = useRef(null);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(true); // Add loading state
  const [dashboardError, setDashboardError] = useState(null);
  
  // Campaign popup state
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [showCampaignConfirmation, setShowCampaignConfirmation] = useState(false); // Add confirmation popup state
  const [showNewCampaignDownloadModal, setShowNewCampaignDownloadModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState(() => getEmptyCampaignForm());
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showDownloadFormatModal, setShowDownloadFormatModal] = useState(false);
  const [isManuallyEditedCampaignName, setIsManuallyEditedCampaignName] = useState(false);

  // Function to update tab title with loading indicator
  const updateTabTitle = (isLoading, originalTitle) => {
    if (isLoading) {
      document.title = '⏳ Downloading... - ' + originalTitle;
      // Also update favicon to show loading state
      const favicon = document.querySelector('link[rel="icon"]') || document.querySelector('link[rel="shortcut icon"]');
      if (favicon) {
        favicon.setAttribute('href', 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">⏳</text></svg>');
      }
    } else {
      document.title = originalTitle;
      // Restore original favicon
      const favicon = document.querySelector('link[rel="icon"]') || document.querySelector('link[rel="shortcut icon"]');
      if (favicon) {
        favicon.setAttribute('href', '/favicon.ico');
      }
    }
  };

  // Memoize dashboard data fetching to prevent unnecessary re-creations
  // IMPORTANT: Define functions BEFORE useEffect hooks that use them
  const fetchDashboardData = useCallback(async () => {
    try {
      const statsResponse = await dashboardClient.get('/stats');
      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
        setLastUpdated(new Date());
        setDashboardError(null);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to load dashboard data';
      setDashboardError(msg);
      toast.error(msg);
      throw error;
    }
  }, []);

  const fetchSubstores = useCallback(async () => {
    try {
      const response = await dashboardClient.get('/substores');
      if (response.data.success) {
        setSubstores(response.data.substores || []);
        setCitySubstores(response.data.citySubstores || {});
        setCityHubs(response.data.cityHubs || {});
        setHubToSubstoreMapping(response.data.hubToSubstoreMapping || []);
        setCities(response.data.cities || []);
        
        // Debug logging for Pan India hubs
        if (response.data.cityHubs && response.data.cityHubs['Pan India']) {
          console.log('📍 Pan India hubs received:', response.data.cityHubs['Pan India']);
          console.log('📍 Pan India hubs count:', response.data.cityHubs['Pan India'].length);
        }
      }
    } catch (error) {
      console.error('Error fetching substores:', error);
      throw error;
    }
  }, []);

  const fetchCampaignNames = useCallback(async () => {
    try {
      const response = await dashboardClient.get('/campaign-names');
      if (response.data.success) {
        setCampaignNames(response.data.campaignNames);
      }
    } catch (error) {
      console.error('Error fetching campaign names:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    // Load only critical data (stats) initially to prevent hanging
    loadCriticalData(fetchDashboardData).catch(error => {
      console.error('Error loading dashboard data:', error);
      setDashboardError(error?.message || 'Failed to load dashboard data');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Load secondary data only when filters are opened (lazy loading)
  // Use ref to prevent infinite loops
  const isLoadingSecondaryData = useRef(false);
  const hasLoadedSecondaryData = useRef(false);
  
  useEffect(() => {
    // Only load if:
    // 1. Filters are shown
    // 2. We haven't already loaded the data
    // 3. We're not currently loading
    // 4. Data is actually missing
    if (showFilters && 
        !hasLoadedSecondaryData.current && 
        !isLoadingSecondaryData.current &&
        (substores.length === 0 || campaignNames.length === 0)) {
      
      isLoadingSecondaryData.current = true;
      
      loadSecondaryData(async () => {
        await Promise.all([
          fetchSubstores(),
          fetchCampaignNames()
        ]);
      }).then(() => {
        hasLoadedSecondaryData.current = true;
        isLoadingSecondaryData.current = false;
      }).catch(() => {
        // Reset flags on error to allow retry
        isLoadingSecondaryData.current = false;
        hasLoadedSecondaryData.current = false;
      });
    }
    // Only depend on showFilters to prevent infinite loops
    // The data length checks are done inside the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-multiselect')) {
        setShowLocationDropdown(false);
      }
      if (showCampaignNameDropdown && !event.target.closest('.campaign-name-multiselect')) {
        setShowCampaignNameDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown, showCampaignNameDropdown]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        toast.success('File selected successfully');
      } else {
        toast.error('Please select a valid CSV file');
        event.target.value = null;
      }
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('csvFile', selectedFile);

    try {
      const response = await axios.post(`/dashboard/upload-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        const stats = response.data.stats || {};
        const processed = stats.createdCount ?? stats.successCount ?? 0;
        const skipped = stats.skippedExistingCount ?? 0;
        const message = stats.skippedExistingCount !== undefined
          ? `${processed} row(s) processed (new customers added). ${skipped} row(s) skipped (already existed).`
          : (response.data.message || 'Upload completed.');
        toast.success(message);
        setSelectedFile(null);
        document.getElementById('file-input').value = '';
        fetchDashboardData(); // Refresh dashboard data
      } else {
        toast.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || 'Upload failed';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Memoize filter change handler to prevent unnecessary re-renders
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const applyFilters = async () => {
    setApplyingFilters(true);
    toast.loading('Applying filters... This may take a moment for large datasets.', { duration: 0 });
    try {
      const params = new URLSearchParams();
      
      // Add all filters except appUserType (we'll handle it separately)
      Object.entries(filters).forEach(([key, value]) => {
        // Skip appUserType and customer here - we add them explicitly below
        if (key === 'appUserType' || key === 'customer') return;
        if (value) params.append(key, value);
      });

      // When user selects only "Blank" for location, filters.substore is '' and was skipped above.
      // Send a sentinel so backend applies blank-only filter (empty string, null, 'default', or missing).
      const onlyBlankSelected =
        selectedLocations?.inScope?.blank &&
        !selectedLocations?.inScope?.delhi?.length &&
        !selectedLocations?.inScope?.bangalore?.length &&
        !selectedLocations?.outScope?.length;
      if (onlyBlankSelected) {
        params.append('substore', '__blank__');
      }

      // Add appUsers parameter based on appUserType
      if (filters.appUserType === 'app') {
        params.append('appUsers', 'true');
      } else if (filters.appUserType === 'non-app') {
        params.append('appUsers', 'false');
      }
      // If appUserType is empty, don't add the parameter (show all users)

      // Add customer filter: yes = customers only (>0 orders), no = non-customers (0 orders)
      if (filters.customer === 'yes') params.append('customer', 'yes');
      else if (filters.customer === 'no') params.append('customer', 'no');

      const response = await filteredCustomersClient.get(`/filtered-customers?${params}`);

      if (response.data.success) {
        setFilteredResults(response.data);
        // Removed excessive debug logging to improve performance
        
        toast.dismiss(); // Dismiss loading toast
        toast.success(`Found ${response.data.totalCount} customers matching your filters`);
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      toast.dismiss(); // Dismiss loading toast
      toast.error('Failed to apply filters');
    } finally {
      setApplyingFilters(false);
    }
  };

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'appUserType' || key === 'customer') return;
      if (value) params.append(key, value);
    });
    const onlyBlankSelected =
      selectedLocations?.inScope?.blank &&
      !selectedLocations?.inScope?.delhi?.length &&
      !selectedLocations?.inScope?.bangalore?.length &&
      !selectedLocations?.outScope?.length;
    if (onlyBlankSelected) params.append('substore', '__blank__');
    if (filters.appUserType === 'app') params.append('appUsers', 'true');
    else if (filters.appUserType === 'non-app') params.append('appUsers', 'false');
    if (filters.customer === 'yes') params.append('customer', 'yes');
    else if (filters.customer === 'no') params.append('customer', 'no');
    return params;
  };

  const stopUserGroupPoll = () => {
    if (userGroupPollRef.current) {
      clearInterval(userGroupPollRef.current);
      userGroupPollRef.current = null;
    }
  };

  useEffect(() => () => stopUserGroupPoll(), []);

  const handleUserGroupUpdate = (data) => {
    if (data.status === 'error') {
      stopUserGroupPoll();
      setCreatingUserGroup(false);
      setUserGroupProgress(null);
      toast.error(data.message || 'Failed to create user group', { id: 'user-group-save' });
      return;
    }
    if (data.status === 'done') {
      stopUserGroupPoll();
      setCreatingUserGroup(false);
      setUserGroupProgress(null);
      toast.success(formatUserGroupSuccess(data), { id: 'user-group-save' });
      setUserGroupName('');
      return;
    }
    setUserGroupProgress(data);
    toast.loading(formatUserGroupProgress(data), { id: 'user-group-save', duration: Infinity });
  };

  const pollUserGroup = (groupName) => {
    stopUserGroupPoll();
    userGroupPollRef.current = setInterval(async () => {
      try {
        const status = await axios.get(
          `/dashboard/filtered-user-group/status?name=${encodeURIComponent(groupName)}`,
          { timeout: 20000 }
        );
        handleUserGroupUpdate(status.data);
      } catch (error) {
        if (error.response?.status !== 404) return;
        stopUserGroupPoll();
        setCreatingUserGroup(false);
        setUserGroupProgress(null);
        toast.error('The save stopped. Click Create User Group again to continue this group.', { id: 'user-group-save' });
      }
    }, 2000);
  };

  const createUserGroup = async () => {
    const name = userGroupName.trim();
    if (!filteredResults) {
      toast.error('Apply filters first');
      return;
    }
    if (!name) {
      toast.error('Enter a user group name');
      return;
    }
    stopUserGroupPoll();
    setCreatingUserGroup(true);
    setUserGroupProgress({ status: 'preparing', total: 0, done: 0, elapsedMs: 0 });
    toast.loading('Preparing the customer list. You can close this tab and continue the same group later.', { id: 'user-group-save' });
    try {
      const params = buildFilterParams();
      const response = await axios.post(`/dashboard/filtered-user-group?${params}`, { name }, { timeout: 0 });
      handleUserGroupUpdate(response.data);
      if (response.data.status !== 'done' && response.data.status !== 'error') {
        pollUserGroup(response.data.groupName || name);
      }
    } catch (error) {
      stopUserGroupPoll();
      setCreatingUserGroup(false);
      setUserGroupProgress(null);
      toast.error(error.response?.data?.message || 'Failed to create user group', { id: 'user-group-save' });
    }
  };

  const clearFilters = () => {
    setFilters({ ...INITIAL_FILTERS_STATE });
    setSelectedLocations({
      inScope: {
        delhi: [],
        bangalore: [],
        blank: false
      },
      outScope: []
    });
    setLocationSearchTerm('');
    setShowLocationDropdown(false);
    setExpandedCategories({
      inScope: false,
      outScope: false
    });
    setExpandedCities({
      delhi: false,
      bangalore: false
    });
    setSelectedCampaignNames([]);
    setCampaignNameSearchTerm('');
    setShowCampaignNameDropdown(false);
    setFilteredResults(null);
  };

  /** Reset one filter card (date range, numeric range, text, selects, location, or campaign multi-select). */
  const clearFilterGroup = useCallback((groupId) => {
    switch (groupId) {
      case 'lastUpdated':
        setFilters((prev) => ({ ...prev, updatedAtFrom: '', updatedAtTo: '' }));
        break;
      case 'createdOn':
        setFilters((prev) => ({ ...prev, createdOnFrom: '', createdOnTo: '' }));
        break;
      case 'firstOrder':
        setFilters((prev) => ({ ...prev, firstOrderFrom: '', firstOrderTo: '' }));
        break;
      case 'lastOrder':
        setFilters((prev) => ({ ...prev, lastOrderFrom: '', lastOrderTo: '' }));
        break;
      case 'lastOrderExclude':
        setFilters((prev) => ({ ...prev, lastOrderExcludeFrom: '', lastOrderExcludeTo: '' }));
        break;
      case 'lifetimeAOV':
        setFilters((prev) => ({ ...prev, lifetimeAOVMin: '', lifetimeAOVMax: '' }));
        break;
      case 'lifetimeGMV':
        setFilters((prev) => ({ ...prev, lifetimeGMVMin: '', lifetimeGMVMax: '' }));
        break;
      case 'totalOrders':
        setFilters((prev) => ({ ...prev, totalOrdersMin: '', totalOrdersMax: '' }));
        break;
      case 'abandonedCartValue':
        setFilters((prev) => ({ ...prev, abandonedCartValueMin: '', abandonedCartValueMax: '' }));
        break;
      case 'abandonedCartLastUpdated':
        setFilters((prev) => ({
          ...prev,
          abandonedCartLastUpdatedFrom: '',
          abandonedCartLastUpdatedTo: ''
        }));
        break;
      case 'campaignName':
        setSelectedCampaignNames([]);
        setFilters((prev) => ({ ...prev, campaignName: '' }));
        break;
      case 'lastCampaignSent':
        setFilters((prev) => ({ ...prev, campaignDateFrom: '', campaignDateTo: '' }));
        break;
      case 'location':
        setSelectedLocations({
          inScope: { delhi: [], bangalore: [], blank: false },
          outScope: []
        });
        setFilters((prev) => ({ ...prev, substore: '' }));
        break;
      case 'emailSearch':
        setFilters((prev) => ({ ...prev, emailSearch: '' }));
        break;
      case 'phoneSearch':
        setFilters((prev) => ({ ...prev, phoneSearch: '' }));
        break;
      case 'appUserType':
        setFilters((prev) => ({ ...prev, appUserType: '' }));
        break;
      case 'customer':
        setFilters((prev) => ({ ...prev, customer: '' }));
        break;
      default:
        break;
    }
  }, []);

  // Helper function to convert hubs to substores
  const convertHubsToSubstores = useCallback((hubs) => {
    if (!hubToSubstoreMapping || !Array.isArray(hubs)) return [];
    return hubs.flatMap(hub => {
      const mapping = hubToSubstoreMapping.find(m => m.hub === hub);
      return mapping ? mapping.substores : [];
    });
  }, [hubToSubstoreMapping]);

  // Location handling functions
  const handleLocationToggle = (category, city, hub) => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      
      if (category === 'inScope') {
        if (city === 'blank') {
          newLocations.inScope.blank = !newLocations.inScope.blank;
        } else {
          const cityKey = city.toLowerCase();
          if (newLocations.inScope[cityKey].includes(hub)) {
            newLocations.inScope[cityKey] = newLocations.inScope[cityKey].filter(h => h !== hub);
          } else {
            newLocations.inScope[cityKey] = [...newLocations.inScope[cityKey], hub];
          }
        }
      } else if (category === 'outScope') {
        if (newLocations.outScope.includes(hub)) {
          newLocations.outScope = newLocations.outScope.filter(h => h !== hub);
        } else {
          newLocations.outScope = [...newLocations.outScope, hub];
        }
      }
      
      // Convert selected hubs to substores for filter
      const delhiSubstores = convertHubsToSubstores(newLocations.inScope.delhi);
      const bangaloreSubstores = convertHubsToSubstores(newLocations.inScope.bangalore);
      const outScopeSubstores = convertHubsToSubstores(newLocations.outScope);
      
      const allSelectedSubstores = [
        ...delhiSubstores,
        ...bangaloreSubstores,
        ...outScopeSubstores
      ];
      
      // Include empty string if blank is selected
      if (newLocations.inScope.blank) {
        allSelectedSubstores.push('');
      }
      
      setFilters(prevFilters => ({
        ...prevFilters,
        substore: allSelectedSubstores.join(',')
      }));
      
      return newLocations;
    });
  };

  const handleSelectAllInScope = (city) => {
    const cityKey = city.toLowerCase();
    // Map lowercase city names to the correct cityHubs keys
    const cityMapping = {
      'delhi': 'Delhi',
      'bangalore': 'Bengaluru'
    };
    const cityHubsList = cityHubs[cityMapping[cityKey]] || [];
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.inScope[cityKey] = cityHubsList;
      
      // Convert selected hubs to substores for filter
      const delhiSubstores = convertHubsToSubstores(newLocations.inScope.delhi);
      const bangaloreSubstores = convertHubsToSubstores(newLocations.inScope.bangalore);
      const outScopeSubstores = convertHubsToSubstores(newLocations.outScope);
      
      const allSelectedSubstores = [
        ...delhiSubstores,
        ...bangaloreSubstores,
        ...outScopeSubstores
      ];
      
      // Include empty string if blank is selected
      if (newLocations.inScope.blank) {
        allSelectedSubstores.push('');
      }
      
      setFilters(prevFilters => ({
        ...prevFilters,
        substore: allSelectedSubstores.join(',')
      }));
      
      return newLocations;
    });
  };

  const handleSelectAllInScopeItems = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      // Select all hubs for Delhi and Bangalore, and set blank to true
      newLocations.inScope.delhi = cityHubs['Delhi'] || [];
      newLocations.inScope.bangalore = cityHubs['Bengaluru'] || [];
      newLocations.inScope.blank = true;
      
      // Convert selected hubs to substores for filter
      const delhiSubstores = convertHubsToSubstores(newLocations.inScope.delhi);
      const bangaloreSubstores = convertHubsToSubstores(newLocations.inScope.bangalore);
      const outScopeSubstores = convertHubsToSubstores(newLocations.outScope);
      
      const allSelectedSubstores = [
        ...delhiSubstores,
        ...bangaloreSubstores,
        ...outScopeSubstores
      ];
      
      // Include empty string if blank is selected
      if (newLocations.inScope.blank) {
        allSelectedSubstores.push('');
      }
      
      setFilters(prevFilters => ({
        ...prevFilters,
        substore: allSelectedSubstores.join(',')
      }));
      
      return newLocations;
    });
  };

  const handleClearAllInScopeItems = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      // Clear all InScope selections
      newLocations.inScope.delhi = [];
      newLocations.inScope.bangalore = [];
      newLocations.inScope.blank = false;
      
      // Convert selected hubs to substores for filter
      const delhiSubstores = convertHubsToSubstores(newLocations.inScope.delhi);
      const bangaloreSubstores = convertHubsToSubstores(newLocations.inScope.bangalore);
      const outScopeSubstores = convertHubsToSubstores(newLocations.outScope);
      
      const allSelectedSubstores = [
        ...delhiSubstores,
        ...bangaloreSubstores,
        ...outScopeSubstores
      ];
      
      // Include empty string if blank is selected
      if (newLocations.inScope.blank) {
        allSelectedSubstores.push('');
      }
      
      setFilters(prevFilters => ({
        ...prevFilters,
        substore: allSelectedSubstores.join(',')
      }));
      
      return newLocations;
    });
  };

  const handleClearAllInScope = (city) => {
    const cityKey = city.toLowerCase();
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.inScope[cityKey] = [];
      
      // Convert selected hubs to substores for filter
      const delhiSubstores = convertHubsToSubstores(newLocations.inScope.delhi);
      const bangaloreSubstores = convertHubsToSubstores(newLocations.inScope.bangalore);
      const outScopeSubstores = convertHubsToSubstores(newLocations.outScope);
      
      const allSelectedSubstores = [
        ...delhiSubstores,
        ...bangaloreSubstores,
        ...outScopeSubstores
      ];
      
      // Include empty string if blank is selected
      if (newLocations.inScope.blank) {
        allSelectedSubstores.push('');
      }
      
      setFilters(prevFilters => ({
        ...prevFilters,
        substore: allSelectedSubstores.join(',')
      }));
      
      return newLocations;
    });
  };

  const handleSelectAllOutScope = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      // Get all InScope hubs (Delhi and Bengaluru)
      const allInScopeHubs = [
        ...(cityHubs.Delhi || []),
        ...(cityHubs.Bengaluru || [])
      ];
      // OutScope includes all hubs except InScope (includes Pan India hubs)
      // Get all hubs from cityHubs and filter out InScope hubs
      const allHubs = [
        ...(cityHubs.Delhi || []),
        ...(cityHubs.Bengaluru || []),
        ...(cityHubs['Pan India'] || [])
      ];
      newLocations.outScope = allHubs.filter(hub => 
        !allInScopeHubs.includes(hub)
      );
      
      // Convert selected hubs to substores for filter
      const delhiSubstores = convertHubsToSubstores(newLocations.inScope.delhi);
      const bangaloreSubstores = convertHubsToSubstores(newLocations.inScope.bangalore);
      const outScopeSubstores = convertHubsToSubstores(newLocations.outScope);
      
      const allSelectedSubstores = [
        ...delhiSubstores,
        ...bangaloreSubstores,
        ...outScopeSubstores
      ];
      
      // Include empty string if blank is selected
      if (newLocations.inScope.blank) {
        allSelectedSubstores.push('');
      }
      
      setFilters(prevFilters => ({
        ...prevFilters,
        substore: allSelectedSubstores.join(',')
      }));
      
      return newLocations;
    });
  };

  const handleClearAllOutScope = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.outScope = [];
      
      // Convert selected hubs to substores for filter
      const delhiSubstores = convertHubsToSubstores(newLocations.inScope.delhi);
      const bangaloreSubstores = convertHubsToSubstores(newLocations.inScope.bangalore);
      const outScopeSubstores = convertHubsToSubstores(newLocations.outScope);
      
      const allSelectedSubstores = [
        ...delhiSubstores,
        ...bangaloreSubstores,
        ...outScopeSubstores
      ];
      
      // Include empty string if blank is selected
      if (newLocations.inScope.blank) {
        allSelectedSubstores.push('');
      }
      
      setFilters(prevFilters => ({
        ...prevFilters,
        substore: allSelectedSubstores.join(',')
      }));
      
      return newLocations;
    });
  };

  const toggleCategoryExpansion = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleCityExpansion = (city) => {
    setExpandedCities(prev => ({
      ...prev,
      [city]: !prev[city]
    }));
  };

  // Campaign name handling functions
  const handleCampaignNameToggle = (campaignName) => {
    setSelectedCampaignNames(prev => {
      if (prev.includes(campaignName)) {
        const newSelected = prev.filter(name => name !== campaignName);
        setFilters(prevFilters => ({
          ...prevFilters,
          campaignName: newSelected.join(',')
        }));
        return newSelected;
      } else {
        const newSelected = [...prev, campaignName];
        setFilters(prevFilters => ({
          ...prevFilters,
          campaignName: newSelected.join(',')
        }));
        return newSelected;
      }
    });
  };

  const handleSelectAllCampaignNames = () => {
    setSelectedCampaignNames(filteredCampaignNames);
    setFilters(prevFilters => ({
      ...prevFilters,
      campaignName: filteredCampaignNames.join(',')
    }));
  };

  const handleClearAllCampaignNames = () => {
    setSelectedCampaignNames([]);
    setFilters(prevFilters => ({
      ...prevFilters,
      campaignName: ''
    }));
  };

  // Debounce search terms to prevent excessive filtering
  const campaignSearchTimeoutRef = useRef(null);
  const locationSearchTimeoutRef = useRef(null);
  const [debouncedCampaignSearchTerm, setDebouncedCampaignSearchTerm] = useState('');
  const [debouncedLocationSearchTerm, setDebouncedLocationSearchTerm] = useState('');
  
  // Debounce campaign name search
  useEffect(() => {
    if (campaignSearchTimeoutRef.current) {
      clearTimeout(campaignSearchTimeoutRef.current);
    }
    
    campaignSearchTimeoutRef.current = setTimeout(() => {
      setDebouncedCampaignSearchTerm(campaignNameSearchTerm);
    }, 300); // 300ms debounce
    
    return () => {
      if (campaignSearchTimeoutRef.current) {
        clearTimeout(campaignSearchTimeoutRef.current);
      }
    };
  }, [campaignNameSearchTerm]);

  // Debounce location search
  useEffect(() => {
    if (locationSearchTimeoutRef.current) {
      clearTimeout(locationSearchTimeoutRef.current);
    }
    
    locationSearchTimeoutRef.current = setTimeout(() => {
      setDebouncedLocationSearchTerm(locationSearchTerm);
    }, 300); // 300ms debounce
    
    return () => {
      if (locationSearchTimeoutRef.current) {
        clearTimeout(locationSearchTimeoutRef.current);
      }
    };
  }, [locationSearchTerm]);

  // Memoize filtered campaign names to prevent unnecessary re-computations
  const filteredCampaignNames = useMemo(() => {
    const term = typeof debouncedCampaignSearchTerm === 'string' ? debouncedCampaignSearchTerm.toLowerCase() : '';
    return campaignNames.filter(campaignName =>
      typeof campaignName === 'string' && campaignName.toLowerCase().includes(term)
    );
  }, [campaignNames, debouncedCampaignSearchTerm]);
  
  // Debug logging (removed to prevent performance issues)
  // console.log('Substores state:', substores);
  // console.log('Selected locations:', selectedLocations);
  // console.log('Show location dropdown:', showLocationDropdown);
  // console.log('Campaign names state:', campaignNames);
  // console.log('Filtered campaign names:', filteredCampaignNames);
  // console.log('Show campaign name dropdown:', showCampaignNameDropdown);

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`/dashboard/csv-template`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  // Campaign form handling functions - memoized to prevent re-renders
  const handleCampaignFormChange = useCallback((field, value) => {
    setCampaignForm(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If user is directly editing the campaign name field, mark it as manually edited
      if (field === 'campaignName') {
        setIsManuallyEditedCampaignName(true);
        return updated;
      }
      
      // Auto-generate campaign name when source, objective, campaignDate, or coupon changes
      // But only if the user hasn't manually edited the campaign name
      if (field === 'source' || field === 'objective' || field === 'campaignDate' || field === 'coupon') {
        // Don't auto-generate if user has manually edited the campaign name
        if (!isManuallyEditedCampaignName) {
          const generatedName = generateCampaignName({
            campaignDate: field === 'campaignDate' ? value : updated.campaignDate,
            source: field === 'source' ? value : updated.source,
            objective: field === 'objective' ? value : updated.objective,
            coupon: field === 'coupon' ? value : updated.coupon
          });
          
          // Only update if we have enough data to generate a name
          if (generatedName) {
            updated.campaignName = generatedName;
          }
        }
      }
      
      return updated;
    });
  }, [isManuallyEditedCampaignName]);

  const handleOfferChange = useCallback((offer) => {
    setCampaignForm(prev => ({
      ...prev,
      offers: prev.offers.includes(offer)
        ? prev.offers.filter(o => o !== offer)
        : [...prev.offers, offer]
    }));
  }, []);

  const handleDownloadWithCampaign = () => {
    if (!filteredResults) {
      toast.error('Please apply filters first');
      return;
    }
    setShowNewCampaignDownloadModal(true); // Show new reusable modal
  };

  const handleDownloadWithoutCampaign = async () => {
    setDownloading(true);
    
    // Add loading indicator to browser tab
    const originalTitle = document.title;
    updateTabTitle(true, originalTitle);
    
    try {
      // Show background download notification
      const downloadToast = toast.loading(
        '📥 Download in progress... This may take 1-2 minutes for large datasets. Please keep this tab open.',
        { 
          duration: 0, // Keep it open until manually dismissed
          id: 'download-progress'
        }
      );

      // Download the file directly without campaign info
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const onlyBlankSelectedDownload =
        selectedLocations?.inScope?.blank &&
        !selectedLocations?.inScope?.delhi?.length &&
        !selectedLocations?.inScope?.bangalore?.length &&
        !selectedLocations?.outScope?.length;
      if (onlyBlankSelectedDownload) params.append('substore', '__blank__');

      // Add appUsers parameter based on appUserType
      if (filters.appUserType === 'app') {
        params.append('appUsers', 'true');
      } else if (filters.appUserType === 'non-app') {
        params.append('appUsers', 'false');
      }

      const response = await axios.get(`/dashboard/download-filtered-customers?${params}`, {
        responseType: 'blob',
        timeout: 120000 // 2 minute timeout for large downloads
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `filtered-customers-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Close confirmation popup
      setShowCampaignConfirmation(false);
      
      // Dismiss loading toast and show success
      toast.dismiss('download-progress');
      toast.success('✅ File downloaded successfully!');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.dismiss('download-progress');
      toast.error(getApiErrorMessage(error, 'Failed to download file'));
    } finally {
      // Restore original tab title
      updateTabTitle(false, originalTitle);
      setDownloading(false);
    }
  };

  const handleConfirmCampaign = () => {
    setShowCampaignConfirmation(false);
    setShowCampaignPopup(true);
    setIsManuallyEditedCampaignName(false); // Reset manual edit flag for new campaign
    setCampaignForm(getEmptyCampaignForm());
  };

  // Handle download with campaign using new modal
  const handleNewDownloadWithCampaign = async (campaignFormData) => {
    // Validate required fields
    if (!campaignFormData.campaignName || !campaignFormData.source || 
        !campaignFormData.pricePerMessage || !campaignFormData.campaignDate || 
        !campaignFormData.objective) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (campaignFormData.source === 'others' && !campaignFormData.sourceRemarks) {
      toast.error('Please provide remarks for "others" source');
      return;
    }

    if (campaignFormData.offers.includes('Other') && !campaignFormData.otherOfferDetails) {
      toast.error('Please provide details for "Other" offer');
      return;
    }

    setDownloading(true);
    
    // Add loading indicator to browser tab
    const originalTitle = document.title;
    updateTabTitle(true, originalTitle);

    try {
      // Fetch customer emails from download endpoint (CSV) for campaign saving
      // This avoids loading all customer data in the filter response
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const onlyBlankForCsv =
        selectedLocations?.inScope?.blank &&
        !selectedLocations?.inScope?.delhi?.length &&
        !selectedLocations?.inScope?.bangalore?.length &&
        !selectedLocations?.outScope?.length;
      if (onlyBlankForCsv) params.append('substore', '__blank__');

      // Add appUsers parameter based on appUserType
      if (filters.appUserType === 'app') {
        params.append('appUsers', 'true');
      } else if (filters.appUserType === 'non-app') {
        params.append('appUsers', 'false');
      }

      // Get CSV data to extract emails
      const csvResponse = await axios.get(`/dashboard/download-filtered-customers?${params}`, {
        responseType: 'text',
        timeout: 120000
      });

      // Parse CSV to get customer emails
      const csvLines = csvResponse.data.split('\n');
      const customerEmails = [];
      
      // Skip header row and process data rows
      for (let i = 1; i < csvLines.length; i++) {
        const line = csvLines[i].trim();
        if (line) {
          const columns = line.split(',');
          if (columns.length >= 3) {
            const email = columns[2].replace(/"/g, '').trim(); // Email is in 3rd column
            if (email && email !== '') {
              customerEmails.push(email);
            }
          }
        }
      }

      const filteredCustomerCount = filteredResults.totalCount || 0;

      console.log('Sending campaign data for customers:', {
        filteredCustomerCount, // Number of customer documents from filter
        customerEmailsCount: customerEmails.length, // Total email addresses extracted
        uniqueEmailsCount: [...new Set(customerEmails)].length,
        note: 'Storing filtered count as metadata to display consistent counts'
      });

      const campaignResponse = await axios.post(`/campaign/save-campaign-info`, {
        customerEmails, // Use emails to save campaign
        filteredCustomerCount, // Send the filtered count as metadata
        ...campaignFormData
      }, {
        timeout: 300000 // 5 min — save-campaign-info can be slow for large cohorts
      });

      if (campaignResponse.data.success) {
        // Show filtered customer count (same as the count shown in filter results)
        const customerCount = campaignResponse.data.successCount || 0;
        
        toast.success(`Campaign saved successfully! Information added to ${customerCount.toLocaleString()} customers`);
        
        // Show background download notification
        const downloadToast = toast.loading(
          '📥 Download in progress... This may take 1-2 minutes for large datasets. Please keep this tab open.',
          { 
            duration: 0, // Keep it open until manually dismissed
            id: 'campaign-download-progress'
          }
        );

        // Download the file
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        const onlyBlankForCampaignDownload =
          selectedLocations?.inScope?.blank &&
          !selectedLocations?.inScope?.delhi?.length &&
          !selectedLocations?.inScope?.bangalore?.length &&
          !selectedLocations?.outScope?.length;
        if (onlyBlankForCampaignDownload) params.append('substore', '__blank__');

        // Add appUsers parameter based on appUserType
        if (filters.appUserType === 'app') {
          params.append('appUsers', 'true');
        } else if (filters.appUserType === 'non-app') {
          params.append('appUsers', 'false');
        }

        const response = await axios.get(`/dashboard/download-filtered-customers?${params}`, {
          responseType: 'blob',
          timeout: 120000 // 2 minute timeout for large downloads
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `filtered-customers-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Dismiss loading toast
        toast.dismiss('campaign-download-progress');

        // Close modal
        setShowNewCampaignDownloadModal(false);

        toast.success('Campaign saved and file downloaded successfully');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.dismiss('campaign-download-progress'); // Dismiss loading toast on error
      toast.error(getApiErrorMessage(error, 'Failed to save campaign information'));
    } finally {
      // Restore original tab title
      updateTabTitle(false, originalTitle);
      setDownloading(false);
    }
  };

  // Handle download without campaign using new modal
  const handleNewDownloadWithoutCampaign = async () => {
    setDownloading(true);
    
    // Add loading indicator to browser tab
    const originalTitle = document.title;
    updateTabTitle(true, originalTitle);
    
    try {
      // Show background download notification
      const downloadToast = toast.loading(
        '📥 Download in progress... This may take 1-2 minutes for large datasets. Please keep this tab open.',
        { 
          duration: 0, // Keep it open until manually dismissed
          id: 'download-progress'
        }
      );

      // Download the file directly without campaign info
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const onlyBlankNewDownload =
        selectedLocations?.inScope?.blank &&
        !selectedLocations?.inScope?.delhi?.length &&
        !selectedLocations?.inScope?.bangalore?.length &&
        !selectedLocations?.outScope?.length;
      if (onlyBlankNewDownload) params.append('substore', '__blank__');

      // Add appUsers parameter based on appUserType
      if (filters.appUserType === 'app') {
        params.append('appUsers', 'true');
      } else if (filters.appUserType === 'non-app') {
        params.append('appUsers', 'false');
      }

      const response = await axios.get(`/dashboard/download-filtered-customers?${params}`, {
        responseType: 'blob',
        timeout: 120000 // 2 minute timeout for large downloads
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `filtered-customers-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Close modal
      setShowNewCampaignDownloadModal(false);
      
      // Dismiss loading toast and show success
      toast.dismiss('download-progress');
      toast.success('✅ File downloaded successfully!');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.dismiss('download-progress');
      toast.error('❌ Failed to download file');
    } finally {
      // Restore original tab title
      updateTabTitle(false, originalTitle);
      setDownloading(false);
    }
  };

  // Close new download modal
  const closeNewDownloadModal = () => {
    setShowNewCampaignDownloadModal(false);
  };

  // Handle opening campaign popup from download modal
  const handleOpenCampaignPopup = () => {
    setShowNewCampaignDownloadModal(false);
    setShowCampaignPopup(true);
    setIsManuallyEditedCampaignName(false); // Reset manual edit flag for new campaign
    setCampaignForm(getEmptyCampaignForm());
  };

  const handleSaveCampaignAndDownload = () => {
    // Validate required fields
    if (!campaignForm.campaignName || !campaignForm.source ||
        !campaignForm.pricePerMessage || !campaignForm.campaignDate ||
        !campaignForm.objective) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (campaignForm.source === 'others' && !campaignForm.sourceRemarks) {
      toast.error('Please provide remarks for "others" source');
      return;
    }

    if (campaignForm.offers.includes('Other') && !campaignForm.otherOfferDetails) {
      toast.error('Please provide details for "Other" offer');
      return;
    }

    setShowDownloadFormatModal(true);
  };

  const handleDownloadFormatSelected = async (format) => {
    const originalTitle = document.title;
    updateTabTitle(true, originalTitle);
    setSavingCampaign(true);
    setDownloading(true);

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    const onlyBlankFormat =
      selectedLocations?.inScope?.blank &&
      !selectedLocations?.inScope?.delhi?.length &&
      !selectedLocations?.inScope?.bangalore?.length &&
      !selectedLocations?.outScope?.length;
    if (onlyBlankFormat) params.append('substore', '__blank__');
    if (filters.appUserType === 'app') {
      params.append('appUsers', 'true');
    } else if (filters.appUserType === 'non-app') {
      params.append('appUsers', 'false');
    }

    try {
      const csvResponse = await axios.get(`/dashboard/download-filtered-customers?${params}`, {
        responseType: 'text',
        timeout: 120000
      });
      const csvText = csvResponse.data;
      const customerEmails = extractEmailsFromCSV(csvText);
      const filteredCustomerCount = filteredResults?.totalCount || 0;

      if (!customerEmails.length) {
        toast.error('No customer emails found in download. Cannot save campaign.');
        return;
      }

      const campaignResponse = await axios.post(`/campaign/save-campaign-info`, {
        customerEmails,
        filteredCustomerCount,
        ...campaignForm
      }, {
        timeout: 300000
      });

      if (!campaignResponse.data.success) {
        toast.error(campaignResponse.data.message || 'Failed to save campaign');
        return;
      }

      const customerCount = campaignResponse.data.successCount || 0;
      toast.success(`Campaign saved! Information added to ${customerCount.toLocaleString()} customers`);

      const downloadToast = toast.loading(
        '📥 Download in progress... Please keep this tab open.',
        { duration: 0, id: 'campaign-download-progress' }
      );

      let blobContent;
      let filename;
      if (format === 'default') {
        blobContent = csvText;
        filename = `filtered-customers-${Date.now()}.csv`;
      } else if (format === 'limechat') {
        blobContent = toLimechatCSV(csvText);
        filename = `filtered-customers-limechat-${Date.now()}.csv`;
      } else {
        blobContent = toEmailOnlyCSV(csvText);
        filename = `filtered-customers-email-${Date.now()}.csv`;
      }

      const url = window.URL.createObjectURL(new Blob([blobContent], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss('campaign-download-progress');
      setShowDownloadFormatModal(false);
      setShowCampaignPopup(false);
      setShowCampaignConfirmation(false);
      setIsManuallyEditedCampaignName(false);
      setCampaignForm(getEmptyCampaignForm());
      toast.success('Campaign saved and file downloaded successfully');
    } catch (error) {
      console.error('Error saving campaign and downloading:', error);
      toast.dismiss('campaign-download-progress');
      toast.error(getApiErrorMessage(error, 'Failed to save campaign information'));
    } finally {
      updateTabTitle(false, originalTitle);
      setSavingCampaign(false);
      setDownloading(false);
    }
  };

  const closeCampaignPopup = () => {
    setShowCampaignPopup(false);
    setShowCampaignConfirmation(false);
    setIsManuallyEditedCampaignName(false); // Reset manual edit flag
    setCampaignForm(getEmptyCampaignForm());
  };

  const closeConfirmationPopup = () => {
    setShowCampaignConfirmation(false);
  };


  return (
    <div className="dashboard-page">
      <Header
        title="Customer Dashboard"
        subtitle="Manage and analyze customer data"
        onRefresh={fetchDashboardData}
        loading={loadingStates.critical || loadingStates.secondary}
        lastUpdated={lastUpdated}
      />

      <div className="dashboard-content">
        {dashboardError && (
          <Alert
            severity="error"
            message={dashboardError}
            action={
              <button
                type="button"
                onClick={() => {
                  setDashboardError(null);
                  loadCriticalData(fetchDashboardData).catch(err => {
                    setDashboardError(err?.message || 'Failed to load dashboard data');
                  });
                }}
                className="retry-button"
              >
                Try Again
              </button>
            }
          />
        )}
        {/* Enhanced Stats Cards Section - KPI Style */}
        <StatsSection stats={stats} loading={loadingStates.critical} />

        {/* Enhanced Customer Filters Section */}
        <FiltersContainer
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          filters={filters}
          setFilters={setFilters}
          handleFilterChange={handleFilterChange}
          substores={substores}
          citySubstores={citySubstores}
          cityHubs={cityHubs}
          cities={cities}
          selectedLocations={selectedLocations}
          setSelectedLocations={setSelectedLocations}
          locationSearchTerm={locationSearchTerm}
          setLocationSearchTerm={setLocationSearchTerm}
          showLocationDropdown={showLocationDropdown}
          setShowLocationDropdown={setShowLocationDropdown}
          expandedCategories={expandedCategories}
          toggleCategoryExpansion={toggleCategoryExpansion}
          expandedCities={expandedCities}
          toggleCityExpansion={toggleCityExpansion}
          campaignNames={campaignNames}
          selectedCampaignNames={selectedCampaignNames}
          setSelectedCampaignNames={setSelectedCampaignNames}
          campaignNameSearchTerm={campaignNameSearchTerm}
          setCampaignNameSearchTerm={setCampaignNameSearchTerm}
          showCampaignNameDropdown={showCampaignNameDropdown}
          setShowCampaignNameDropdown={setShowCampaignNameDropdown}
          filteredResults={filteredResults}
          applyingFilters={applyingFilters}
          loading={loadingStates.critical || loadingStates.secondary}
          downloading={downloading}
          applyFilters={applyFilters}
          handleDownloadWithCampaign={handleDownloadWithCampaign}
          userGroupName={userGroupName}
          setUserGroupName={setUserGroupName}
          creatingUserGroup={creatingUserGroup}
          userGroupProgressLabel={userGroupProgress ? formatUserGroupProgress(userGroupProgress) : ''}
          createUserGroup={createUserGroup}
          clearFilters={clearFilters}
          clearFilterGroup={clearFilterGroup}
          handleLocationToggle={handleLocationToggle}
          handleSelectAllInScope={handleSelectAllInScope}
          handleClearAllInScope={handleClearAllInScope}
          handleSelectAllInScopeItems={handleSelectAllInScopeItems}
          handleClearAllInScopeItems={handleClearAllInScopeItems}
          handleSelectAllOutScope={handleSelectAllOutScope}
          handleClearAllOutScope={handleClearAllOutScope}
          handleCampaignNameToggle={handleCampaignNameToggle}
          handleSelectAllCampaignNames={handleSelectAllCampaignNames}
          handleClearAllCampaignNames={handleClearAllCampaignNames}
          filteredCampaignNames={filteredCampaignNames}
        />

        {/* Enhanced File Upload Section */}
        <UploadSection
          selectedFile={selectedFile}
          uploading={uploading}
          uploadProgress={uploadProgress}
          loading={loadingStates.critical || loadingStates.secondary}
          handleFileSelect={handleFileSelect}
          handleFileUpload={handleFileUpload}
          downloadTemplate={downloadTemplate}
        />
        </div>

      {/* Campaign Popup Modal */}
      <CampaignPopup
        showCampaignPopup={showCampaignPopup}
        campaignForm={campaignForm}
        handleCampaignFormChange={handleCampaignFormChange}
        handleOfferChange={handleOfferChange}
        handleSaveCampaignAndDownload={handleSaveCampaignAndDownload}
        closeCampaignPopup={closeCampaignPopup}
        savingCampaign={savingCampaign}
      />

      {/* Campaign Confirmation Popup */}
      <ConfirmationPopup
        showCampaignConfirmation={showCampaignConfirmation}
        handleDownloadWithoutCampaign={handleDownloadWithoutCampaign}
        handleConfirmCampaign={handleConfirmCampaign}
        closeConfirmationPopup={closeConfirmationPopup}
        downloading={downloading}
      />

      {/* New Campaign Download Modal */}
      <CampaignDownloadModal
        showModal={showNewCampaignDownloadModal}
        onClose={closeNewDownloadModal}
        onDownloadWithoutCampaign={handleNewDownloadWithoutCampaign}
        onDownloadWithCampaign={handleOpenCampaignPopup}
        downloading={downloading}
        customerCount={filteredResults?.totalCount || 0}
        campaignType="filtered"
      />

      {/* Download format choice (after Save & Download in Campaign Popup) */}
      <DownloadFormatModal
        showModal={showDownloadFormatModal}
        onClose={() => setShowDownloadFormatModal(false)}
        onSelectFormat={handleDownloadFormatSelected}
        downloading={downloading}
      />
      </div>
    );
  };

export default Dashboard; 